import { useState } from 'react';
import { Ship, Anchor, AlertTriangle, LifeBuoy, Plus, Trash2, RefreshCw, MessageSquare, CheckCircle2, Clock, Wifi, WifiOff } from 'lucide-react';
import type { UseSpeedBoatLiveSessionResult } from '../../../hooks/useSpeedBoatLiveSession';
import type { GameEvent, LiveSpeedBoatFactor, SpeedBoatConfig, SpeedBoatFactorType } from '../../../types/collaborativeGame';

interface SpeedBoatLiveBoardViewProps {
  game: UseSpeedBoatLiveSessionResult;
  myName: string;
  isHost: boolean;
  onEndSession: () => void;
}

const TYPE_CONFIG = {
  engine: {
    label: 'Engines',
    sublabel: 'Driving forward',
    Icon: Ship,
    bg: 'bg-[var(--slate-dim)]',
    border: 'border-[var(--slate)]',
    text: 'text-[var(--text-sec)]',
    iconColor: 'text-[var(--text-ter)]',
    addBg: 'bg-[var(--slate)] hover:bg-[var(--slate)]',
    pillBg: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  },
  anchor: {
    label: 'Anchors',
    sublabel: 'Holding back',
    Icon: Anchor,
    bg: 'bg-[var(--coral-dim)]',
    border: 'border-[var(--coral)]',
    text: 'text-[var(--coral)]',
    iconColor: 'text-[var(--coral)]',
    addBg: 'bg-[var(--coral)] hover:bg-[var(--coral)]',
    pillBg: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  },
  rock: {
    label: 'Rocks',
    sublabel: 'Risks ahead',
    Icon: AlertTriangle,
    bg: 'bg-[var(--mustard-dim)]',
    border: 'border-[var(--mustard)]',
    text: 'text-[var(--mustard)]',
    iconColor: 'text-[var(--mustard)]',
    addBg: 'bg-[var(--mustard)] hover:bg-[var(--mustard)]',
    pillBg: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  },
  preserver: {
    label: 'Life Preservers',
    sublabel: 'Risk mitigations',
    Icon: LifeBuoy,
    bg: 'bg-[var(--green-dim)]',
    border: 'border-[var(--green)]',
    text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    iconColor: 'text-[var(--green)]',
    addBg: 'bg-[var(--green)] hover:bg-[var(--green)]',
    pillBg: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  },
} as const;

interface AddModal {
  type: SpeedBoatFactorType;
  name: string;
  note: string;
}

interface ReclassifyModal {
  factor: LiveSpeedBoatFactor;
  toType: SpeedBoatFactorType;
  note: string;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function TranscriptItem({ event }: { event: GameEvent }) {
  const data = event.eventData as Record<string, unknown>;

  const label = () => {
    switch (event.eventType) {
      case 'join':
        return <span><strong>{event.participantName}</strong> joined{(data.isHost as boolean) ? ' (host)' : ''}</span>;
      case 'session_started':
        return <span>Session started</span>;
      case 'add_factor': {
        const cfg = TYPE_CONFIG[data.factorType as SpeedBoatFactorType];
        const Icon = cfg.Icon;
        return (
          <span>
            <strong>{event.participantName}</strong> added{' '}
            <span className={`inline-flex items-center gap-1 ${cfg.pillBg} px-1.5 py-0.5 rounded text-xs`}>
              <Icon className="h-3 w-3" />{data.factorType as string}
            </span>{' '}
            <strong>{data.factorName as string}</strong>
            {data.note && <span className="block text-[var(--text-ter)] text-xs mt-0.5 italic">"{data.note as string}"</span>}
          </span>
        );
      }
      case 'remove_factor':
        return <span><strong>{event.participantName}</strong> removed <strong>{data.factorName as string}</strong></span>;
      case 'reclassify_factor': {
        const fromCfg = TYPE_CONFIG[data.fromType as SpeedBoatFactorType];
        const toCfg = TYPE_CONFIG[data.toType as SpeedBoatFactorType];
        const FromIcon = fromCfg.Icon;
        const ToIcon = toCfg.Icon;
        return (
          <span>
            <strong>{event.participantName}</strong> moved <strong>{data.factorName as string}</strong>{' '}
            <span className={`inline-flex items-center gap-1 ${fromCfg.pillBg} px-1 rounded text-xs`}><FromIcon className="h-3 w-3" />{data.fromType as string}</span>
            {' → '}
            <span className={`inline-flex items-center gap-1 ${toCfg.pillBg} px-1 rounded text-xs`}><ToIcon className="h-3 w-3" />{data.toType as string}</span>
            {data.note && <span className="block text-[var(--text-ter)] text-xs mt-0.5 italic">"{data.note as string}"</span>}
          </span>
        );
      }
      case 'comment':
        return <span><strong>{event.participantName}</strong>: <em>{data.text as string}</em></span>;
      case 'done':
        return <span><strong>{event.participantName}</strong> finished</span>;
      case 'session_ended':
        return <span className="text-[var(--text-ter)]">Session ended</span>;
      default:
        return <span>{event.eventType}</span>;
    }
  };

  return (
    <div className="flex gap-2 text-sm py-1 border-b border-[var(--rule)] last:border-0">
      <span className="text-[var(--text-ter)] text-xs shrink-0 pt-0.5 font-mono">{formatTime(event.createdAt)}</span>
      <span className="text-[var(--text-sec)]">{label()}</span>
    </div>
  );
}

function FactorCard({
  factor,
  myName,
  onRemove,
  onReclassify,
}: {
  factor: LiveSpeedBoatFactor;
  myName: string;
  onRemove: () => void;
  onReclassify: () => void;
}) {
  const cfg = TYPE_CONFIG[factor.type];
  const isMine = factor.addedBy === myName;

  return (
    <div className={`p-3  border ${cfg.bg} ${cfg.border} ${cfg.text} group`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm leading-tight">{factor.name}</div>
          {factor.note && (
            <div className="text-xs opacity-70 mt-0.5 italic">"{factor.note}"</div>
          )}
          <div className="text-xs opacity-60 mt-1">by {factor.addedBy}</div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={onReclassify}
            title="Move to different category"
            className="p-1 rounded hover:bg-[var(--bg-raised)]/60 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          {isMine && (
            <button
              onClick={onRemove}
              title="Remove"
              className="p-1 rounded hover:bg-[var(--bg-raised)]/60 transition-colors text-[var(--coral)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function SpeedBoatLiveBoardView({ game, myName, isHost, onEndSession }: SpeedBoatLiveBoardViewProps) {
  const [addModal, setAddModal] = useState<AddModal | null>(null);
  const [reclassifyModal, setReclassifyModal] = useState<ReclassifyModal | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);

  const config = game.session?.config as SpeedBoatConfig | undefined;
  const me = game.participants.find(p => p.name === myName);
  const myDone = me?.isDone ?? false;
  const donePct = game.participants.length > 0
    ? Math.round((game.participants.filter(p => p.isDone).length / game.participants.length) * 100)
    : 0;

  const factorsByType = (type: SpeedBoatFactorType) =>
    game.factors.filter(f => f.type === type);

  const handleAddFactor = async () => {
    if (!addModal || !addModal.name.trim()) return;
    await game.addFactor(addModal.name, addModal.type, addModal.note || undefined);
    setAddModal(null);
  };

  const handleReclassify = async () => {
    if (!reclassifyModal) return;
    const { factor, toType, note } = reclassifyModal;
    await game.reclassifyFactor(factor.id, factor.name, factor.type, toType, note || undefined);
    setReclassifyModal(null);
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await game.addComment(commentText.trim());
    setCommentText('');
    setShowCommentBox(false);
  };

  const types: SpeedBoatFactorType[] = ['engine', 'anchor', 'rock', 'preserver'];

  return (
    <div className="flex h-full gap-0">
      {/* Board */}
      <div className="flex-1 overflow-y-auto p-4 min-w-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              {game.isConnected
                ? <Wifi className="h-4 w-4 text-[var(--green)]" />
                : <WifiOff className="h-4 w-4 text-[var(--coral)]" />}
              <span className="text-sm text-[var(--text-ter)]">{game.isConnected ? 'Live' : 'Reconnecting…'}</span>
            </div>
            {config?.topic && (
              <div className="text-sm font-medium text-[var(--text-sec)]">{config.topic}</div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {!myDone && (
              <button
                onClick={() => game.markDone()}
                className="px-3 py-1.5 text-sm bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors flex items-center gap-1"
              >
                <CheckCircle2 className="h-4 w-4" />
                Done
              </button>
            )}
            {isHost && (
              <button
                onClick={onEndSession}
                className="px-3 py-1.5 text-sm bg-[var(--slate)] text-white hover:bg-[var(--bg-raised)] transition-colors"
              >
                End session
              </button>
            )}
          </div>
        </div>

        {/* Participants */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {game.participants.map(p => (
            <div
              key={p.name}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                p.isDone ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)]'
              }`}
            >
              {p.isDone ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3 text-[var(--text-ter)]" />}
              {p.name}
            </div>
          ))}
        </div>

        {/* Progress */}
        {game.participants.length > 0 && (
          <div className="mb-4">
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-1">
              <div className="bg-[var(--green)] h-1 rounded-full transition-all" style={{ width: `${donePct}%` }} />
            </div>
          </div>
        )}

        {/* Four-quadrant board */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {types.map(type => {
            const cfg = TYPE_CONFIG[type];
            const Icon = cfg.Icon;
            const typeFactors = factorsByType(type);

            return (
              <div key={type} className={` border-2 ${cfg.border} ${cfg.bg} p-3 min-h-[160px]`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`h-4 w-4 ${cfg.iconColor}`} />
                    <span className={`font-semibold text-sm ${cfg.text}`}>{cfg.label}</span>
                    <span className={`text-xs opacity-60 ${cfg.text}`}>({typeFactors.length})</span>
                  </div>
                  {!myDone && (
                    <button
                      onClick={() => setAddModal({ type, name: '', note: '' })}
                      className={`flex items-center gap-1 px-2 py-1 ${cfg.addBg} text-white text-xs  transition-colors`}
                    >
                      <Plus className="h-3 w-3" />
                      Add
                    </button>
                  )}
                </div>
                <p className={`text-xs opacity-60 mb-2 ${cfg.text}`}>{cfg.sublabel}</p>
                <div className="space-y-2">
                  {typeFactors.map(factor => (
                    <FactorCard
                      key={factor.id}
                      factor={factor}
                      myName={myName}
                      onRemove={() => game.removeFactor(factor.id, factor.name)}
                      onReclassify={() => setReclassifyModal({ factor, toType: type === 'engine' ? 'anchor' : 'engine', note: '' })}
                    />
                  ))}
                  {typeFactors.length === 0 && (
                    <p className={`text-xs italic opacity-40 ${cfg.text}`}>Nothing here yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Comment */}
        <div className="mt-4">
          {showCommentBox ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Say something to the group…"
                className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-purple-500"
                onKeyDown={e => e.key === 'Enter' && handleComment()}
                autoFocus
              />
              <button onClick={handleComment} disabled={!commentText.trim()} className="px-3 py-2 bg-[var(--slate)] text-white text-sm hover:bg-[var(--slate)] disabled:opacity-50">Send</button>
              <button onClick={() => setShowCommentBox(false)} className="px-3 py-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowCommentBox(true)} className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]">
              <MessageSquare className="h-4 w-4" />
              Add comment to transcript
            </button>
          )}
        </div>
      </div>

      {/* Live transcript sidebar */}
      <div className="w-64 shrink-0 border-l border-[var(--rule)] bg-[var(--surface)] flex flex-col">
        <div className="px-3 py-2 border-b border-[var(--rule)]">
          <h3 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Live transcript</h3>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {game.events.length === 0
            ? <p className="text-xs text-[var(--text-ter)] pt-2">Waiting for activity…</p>
            : [...game.events].reverse().map(event => <TranscriptItem key={event.id} event={event} />)
          }
        </div>
      </div>

      {/* Add factor modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-sm w-full p-6">
            {(() => {
              const cfg = TYPE_CONFIG[addModal.type];
              const Icon = cfg.Icon;
              return (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className={`h-5 w-5 ${cfg.iconColor}`} />
                    <h3 className="text-lg font-bold text-[var(--text-pri)]">Add {cfg.label.slice(0, -1)}</h3>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">What is it?</label>
                      <input
                        type="text"
                        value={addModal.name}
                        onChange={e => setAddModal(m => m ? { ...m, name: e.target.value } : null)}
                        placeholder={cfg.sublabel + '…'}
                        className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleAddFactor()}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                        Why? <span className="text-[var(--text-ter)] font-normal">(optional — goes in the transcript)</span>
                      </label>
                      <input
                        type="text"
                        value={addModal.note}
                        onChange={e => setAddModal(m => m ? { ...m, note: e.target.value } : null)}
                        placeholder="e.g. This blocked us for two sprints"
                        className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={handleAddFactor} disabled={!addModal.name.trim()} className={`flex-1 px-4 py-2.5 ${cfg.addBg} text-white  font-medium transition-colors disabled:opacity-50`}>
                      Add to board
                    </button>
                    <button onClick={() => setAddModal(null)} className="flex-1 px-4 py-2.5 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] font-medium transition-colors">
                      Cancel
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Reclassify modal */}
      {reclassifyModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-[var(--text-pri)] mb-1">Move "{reclassifyModal.factor.name}"</h3>
            <p className="text-sm text-[var(--text-sec)] mb-4">
              Currently: <strong>{TYPE_CONFIG[reclassifyModal.factor.type].label}</strong>
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Move to</label>
                <select
                  value={reclassifyModal.toType}
                  onChange={e => setReclassifyModal(m => m ? { ...m, toType: e.target.value as SpeedBoatFactorType } : null)}
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {types.filter(t => t !== reclassifyModal.factor.type).map(t => (
                    <option key={t} value={t}>{TYPE_CONFIG[t].label} — {TYPE_CONFIG[t].sublabel}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Why the change? <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reclassifyModal.note}
                  onChange={e => setReclassifyModal(m => m ? { ...m, note: e.target.value } : null)}
                  placeholder="e.g. Actually this is a risk, not a blocker"
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500"
                  onKeyDown={e => e.key === 'Enter' && handleReclassify()}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleReclassify} className="flex-1 px-4 py-2.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] font-medium transition-colors">
                Move
              </button>
              <button onClick={() => setReclassifyModal(null)} className="flex-1 px-4 py-2.5 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] font-medium transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
